<script setup lang="ts">
import { useBusinessEntitiesStore } from "@/stores/business-entity-store";
import { storeToRefs } from "pinia";
import { useConfirm } from "primevue/useconfirm";
import { useEditEntityDialog } from "@/composables/edit-entity-dialog";

const props = defineProps({ isEditing: Boolean });

const { entities, isLoading } = storeToRefs(useBusinessEntitiesStore());
const { deleteEntity } = useBusinessEntitiesStore();

const { open: openEditEntity } = useEditEntityDialog();

const confirm = useConfirm();

const deleteBusinessEntity = async (event: Event, index: number) => {
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: `
            After deletion of '${entities.value[index].metadata.name}'
            as definition, you cannot reference it anymore. Are you sure?
        `,
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            await deleteEntity(entities.value[index].identity);
        },
    });
};
</script>

<template>
    <DataTable
        ref="dataTable"
        :loading="isLoading"
        :value="entities"
        selection-mode="single"
        dataKey="identity"
        :rowHover="true"
        :rowEditor="isEditing"
    >
        <template #empty> No entities found. </template>
        <template #loading> Fetching data... </template>
        <template #header>
            <div style="display: flex; justify-content: space-between">
                <h2>Definitions</h2>
                <Button
                    v-if="props.isEditing"
                    label="Add definition"
                    icon="pi pi-plus"
                    text
                    size="small"
                    severity="secondary"
                    @click="openEditEntity()"
                />
            </div>
        </template>
        <Column field="metadata.name" header="Name" class="name-col" />
        <Column field="metadata.description" header="Description" />
        <Column field="" header="" class="buttons-col">
            <template #body="{ index, data }" v-if="isEditing">
                <Button
                    severity="secondary"
                    type="button"
                    text
                    rounded
                    icon="pi pi-pencil"
                    :loading="isLoading"
                    @click="
                        (e: MouseEvent) => {
                            e.stopPropagation();
                            openEditEntity(data);
                        }
                    "
                />
                <Button
                    severity="danger"
                    type="button"
                    text
                    rounded
                    icon="pi pi-trash"
                    :loading="isLoading"
                    @click="
                        (e: MouseEvent) => {
                            deleteBusinessEntity(e, index);
                        }
                    "
                />
            </template>
        </Column>
    </DataTable>
</template>

<style scoped lang="scss">
:deep(.p-datatable-thead) > tr > th,
:deep(.p-datatable-tbody) > tr > td {
    &.buttons-col {
        min-width: 50px;
        text-align: right;
    }
    &.name-col {
        width: 30rem;
    }
}
:deep(.p-datatable-tbody) > tr > td.buttons-col {
    padding-top: 0;
    padding-bottom: 0;
}
</style>
